import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useQuery } from "@tanstack/react-query";
import { base44 } from "@/api/base44Client";
import { AlertCircle, Clock, Users } from "lucide-react";

export default function TripContextSetup({ port, user, onSave, onBack }) {
  const [formData, setFormData] = useState({
    boatId: "",
    eta: "",
    etaTime: "12:00",
    arrivalMode: "motor",
    crewCount: 1,
    isCharter: false
  });

  const { data: boats = [] } = useQuery({
    queryKey: ["user-boats"],
    queryFn: () => base44.entities.Boat.filter({ created_by: user.email })
  });

  const { data: trips = [] } = useQuery({
    queryKey: ["user-trips"],
    queryFn: () => base44.entities.Trip.filter({ created_by: user.email }, "-start_time", 5)
  });

  useEffect(() => {
    if (boats.length > 0) {
      setFormData(prev => ({ ...prev, boatId: boats[0].id }));
    }
  }, [boats]);

  const [errors, setErrors] = useState({});
  const selectedBoat = boats.find(b => b.id === formData.boatId);
  const lastTrip = trips[0];

  const handleSave = () => {
    const newErrors = {};
    if (!formData.boatId) newErrors.boatId = "Please select a vessel.";
    if (!formData.eta) newErrors.eta = "Please enter an arrival date.";
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    setErrors({});
    onSave({
      ...formData,
      boat: selectedBoat,
      lastPort: lastTrip?.start_location || "Unknown"
    });
  };

  return (
    <div className="space-y-6">
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <Card className="bg-gradient-to-r from-blue-50 to-cyan-50 dark:from-blue-950/30 dark:to-cyan-950/30 border-blue-200 dark:border-blue-800">
          <CardContent className="pt-6">
            <div className="flex gap-3 items-start">
              <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-semibold text-blue-900 dark:text-blue-100 mb-1">Smart Auto-Fill</p>
                <p className="text-blue-800 dark:text-blue-200 text-xs">We've pre-filled your vessel and last trip info. Edit as needed.</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      <motion.div
        className="space-y-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.1 }}
      >
        {/* Boat Selection */}
        <div>
          <label className="text-sm font-semibold mb-2 block">Vessel</label>
          <Select value={formData.boatId} onValueChange={(v) => setFormData({...formData, boatId: v})}>
            <SelectTrigger>
              <SelectValue placeholder="Select vessel…" />
            </SelectTrigger>
            <SelectContent>
              {boats.map(boat => (
                <SelectItem key={boat.id} value={boat.id}>
                  {boat.name} ({boat.type})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {selectedBoat && (
            <p className="text-xs text-muted-foreground mt-1">
              📍 {selectedBoat.registration_number} • {selectedBoat.model}
            </p>
          )}
          {errors.boatId && (
            <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
              <AlertCircle className="w-3 h-3" /> {errors.boatId}
            </p>
          )}
        </div>

        {/* ETA */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-semibold mb-2 block flex items-center gap-1">
              <Clock className="w-4 h-4" /> Arrival Date
            </label>
            <Input
              type="date"
              value={formData.eta}
              onChange={(e) => setFormData({...formData, eta: e.target.value})}
              required
              className={errors.eta ? "border-red-500" : ""}
            />
            {errors.eta && (
              <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                <AlertCircle className="w-3 h-3" /> {errors.eta}
              </p>
            )}
          </div>
          <div>
            <label className="text-sm font-semibold mb-2 block">Arrival Time</label>
            <Input
              type="time"
              value={formData.etaTime}
              onChange={(e) => setFormData({...formData, etaTime: e.target.value})}
            />
          </div>
        </div>

        {/* Arrival Mode */}
        <div>
          <label className="text-sm font-semibold mb-2 block">Arrival Mode</label>
          <div className="grid grid-cols-2 gap-3">
            {[
              { value: "sailing", label: "⛵ Sailing" },
              { value: "motor", label: "🚤 Motor" }
            ].map(mode => (
              <button
                key={mode.value}
                onClick={() => setFormData({...formData, arrivalMode: mode.value})}
                className={`p-3 rounded-lg border-2 transition-all text-sm font-semibold ${
                  formData.arrivalMode === mode.value
                    ? "border-primary bg-primary/10 text-primary"
                    : "border-border bg-background hover:border-muted-foreground"
                }`}
              >
                {mode.label}
              </button>
            ))}
          </div>
        </div>

        {/* Crew Count */}
        <div>
          <label className="text-sm font-semibold mb-2 block flex items-center gap-1">
            <Users className="w-4 h-4" /> Crew Count
          </label>
          <Input
            type="number"
            min="1"
            value={formData.crewCount}
            onChange={(e) => setFormData({...formData, crewCount: parseInt(e.target.value)})}
          />
        </div>

        {/* Charter Toggle */}
        <label className="flex items-center gap-3 p-3 rounded-lg border border-border hover:bg-muted/50 cursor-pointer">
          <input
            type="checkbox"
            checked={formData.isCharter}
            onChange={(e) => setFormData({...formData, isCharter: e.target.checked})}
            className="w-4 h-4 rounded"
          />
          <div>
            <p className="text-sm font-semibold">Charter Vessel</p>
            <p className="text-xs text-muted-foreground">This is a chartered boat</p>
          </div>
        </label>
      </motion.div>

      {/* Actions */}
      <div className="flex gap-3 justify-end sticky bottom-0 bg-background pt-4 border-t border-border">
        <Button variant="outline" onClick={onBack}>Back</Button>
        <Button onClick={handleSave} className="gap-2">
          Continue to Entry Pack →
        </Button>
      </div>
    </div>
  );
}